// A form component with error and success messages
// Posts form data to lambda-emailer program

// All CMSName props should exist as names in a "Forms" table in Airtable

import React, { useState } from 'react'
import axios from 'axios'
import styled from 'styled-components'

import { getCMSText } from '@talus-analytics/library.airtable.cms-text'
import useContactPageData from 'cmsHooks/useContactPageData'

import { Center } from 'components/layout'
import { ActionButton } from 'components/ui'
import { flattenDiagnosticMessageText } from 'typescript'

const Message = styled.p`
  box-sizing: border-box;
  margin-top: 0rem;
  margin-bottom: 0rem;
  font-weight: 500;
  padding: 1rem;
  border-radius: 5px;
`
const ErrorMessage = styled(Message)`
  color: #862526 !important;
  background-color: #fff2f2;
  border: 1px solid #ffd2d2;
  margin-top: 1rem;
  font-size: 1rem;
  width: 100%;
`
const SuccessMessage = styled(Message)`
  color: #328625 !important;
  background-color: #f2fff3;
  border: 1px solid#7abd70;
  font-size: 1rem;
  margin-top: 1rem;
`
const Flex = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
`

interface EmailerFormProps {
  subjectCMSName: string
  siteCMSName: string
  bodyCMSName: string
  buttonTextCMSName: string
  submittingButtonTextCMSName: string
  successMessageCMSName: string
  errorMessageCMSName: string
  submitUrlCMSName: string
  formAriaLabelCMSName: string
  buttonLeft?: boolean
  children: JSX.Element | JSX.Element[]
}

const EmailerForm = ({
  subjectCMSName,
  siteCMSName,
  bodyCMSName,
  buttonTextCMSName,
  submittingButtonTextCMSName,
  successMessageCMSName,
  errorMessageCMSName,
  submitUrlCMSName,
  formAriaLabelCMSName,
  buttonLeft = false,
  children,
}: EmailerFormProps): JSX.Element => {
  const data = useContactPageData()

  const [buttonText, setButtonText] = useState(
    getCMSText(data, buttonTextCMSName)
  )
  const [successMessage, setSuccessMessage] = React.useState<
    string | React.ReactElement
  >('')
  const [errorMessage, setErrorMessage] = React.useState<
    string | React.ReactElement
  >('')

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const d = Object.fromEntries(new FormData(e.target as HTMLFormElement))
    d['subject'] = getCMSText(data, subjectCMSName)
    d['site'] = getCMSText(data, siteCMSName)
    d['body'] = getCMSText(data, bodyCMSName)

    // reset messages and change button text as form is submitted
    setErrorMessage('')
    setSuccessMessage('')
    setButtonText(getCMSText(data, submittingButtonTextCMSName))

    // submit request to emailer
    axios
      .post(getCMSText(data, submitUrlCMSName), JSON.stringify(d), {
        headers: { 'Content-Type': 'application/json' },
      })
      .then(() => {
        // success
        setSuccessMessage(
          <SuccessMessage>
            {getCMSText(data, successMessageCMSName)}
          </SuccessMessage>
        )
        setButtonText(getCMSText(data, buttonTextCMSName))
      })
      .catch(error => {
        // error
        console.log(error)
        setErrorMessage(
          <ErrorMessage>{getCMSText(data, errorMessageCMSName)}</ErrorMessage>
        )
        setButtonText(getCMSText(data, buttonTextCMSName))
      })
  }
  return (
    <form
      onSubmit={handleSubmit}
      aria-label={getCMSText(data, formAriaLabelCMSName)}
    >
      {children}
      {buttonLeft ? (
        <Flex>
          <ActionButton>{buttonText}</ActionButton>
          {errorMessage}
          {successMessage}
        </Flex>
      ) : (
        <Center cross>
          <ActionButton>{buttonText}</ActionButton>
          {errorMessage}
          {successMessage}
        </Center>
      )}
    </form>
  )
}

export default EmailerForm
